import getFeatureDecision from '../../../../config';
import { COMBO_RECOMMENDATION_TYPE } from '../../../../config/constants';
import { logWarnWrapper } from '../../../../services/logWrapper';
import {
  buildRoute,
  formatMoney,
  getPricePerDay,
} from '../../components/RecommendedForYou/utils';
import { COMPONENT_CAPYBARA_RECOMMENDATIONS } from '../constants/analyticsConstants';
import { CAR_HIRE_PROVIDER_LOGO_BASE_URL } from '../constants/urlConstants';
import {
  buildAirlineLogoUrl,
  buildCarHireRedirectUrl,
  buildFlightRedirectUrl,
  buildHotelRedirectUrl,
} from '../interface-handling/buildUrls';
import { normaliseMoneyAmount } from '../presentation/normaliseMoneyAmount';

import { transformHotelContent } from './transformHotelContent';

import type { LocalisationHelpers } from '../../../../bootstrap/localisation';
import type { GetResultUrl } from '../../../../services/contextAdaptor/types';
import type {
  CarHireInfo,
  ComboRecommendation,
  FlightInfo,
  FlightLegInfo,
  HotelInfo,
} from '../../components/RecommendedForYou/types';
import type {
  CarHireResult,
  HotelContent,
} from '../../selectors/michelinTypes';
import type { StayState } from '../../selectors/types';
import type { SkyscannerMichelinRecommendationsV1Recommendations } from '@skyscanner-internal/michelin-web-http-client';

type FlightResult =
  SkyscannerMichelinRecommendationsV1Recommendations.FlightResult;
type HotelResult =
  SkyscannerMichelinRecommendationsV1Recommendations.HotelResult;
type Recommendation =
  SkyscannerMichelinRecommendationsV1Recommendations.Recommendation;

const roundAmount = (amount: number | null): number | null =>
  amount == null ? null : Math.round(amount);

type BuildFlightInfoParams = {
  flightResult: FlightResult;
  formatCurrency: LocalisationHelpers['formatCurrency'];
  onClickUrl: string;
  originId: string | undefined;
  destinationId: string | undefined;
};

const buildFlightInfo = ({
  destinationId,
  flightResult,
  formatCurrency,
  onClickUrl,
  originId,
}: BuildFlightInfoParams): FlightInfo => {
  if (!flightResult.price) {
    logWarnWrapper({
      message: 'Flight result missing price',
      component: COMPONENT_CAPYBARA_RECOMMENDATIONS,
      hasInbound: flightResult.inbound ? '1' : '0',
      carrierId: flightResult.outbound?.carrierId || 'unknown',
    });
  }

  const isDirect = flightResult.direct ?? false;

  const outboundDeparture =
    flightResult.outbound?.departureAirportCode || originId;
  const outboundArrival =
    flightResult.outbound?.arrivalAirportCode || destinationId;
  const inboundDeparture =
    flightResult.inbound?.departureAirportCode || destinationId;
  const inboundArrival = flightResult.inbound?.arrivalAirportCode || originId;

  const outbound: FlightLegInfo = {
    airlineLogoUrl: buildAirlineLogoUrl(flightResult.outbound?.airlineCode),
    airlineName: flightResult.outbound?.carrierName,
    route: buildRoute(outboundDeparture, outboundArrival),
    isDirect,
    direction: 'departure',
  };

  const inbound: FlightLegInfo | undefined = flightResult.inbound
    ? {
        airlineLogoUrl: buildAirlineLogoUrl(flightResult.inbound?.airlineCode),
        airlineName: flightResult.inbound?.carrierName,
        route: buildRoute(inboundDeparture, inboundArrival),
        isDirect,
        direction: 'return',
      }
    : undefined;

  return {
    outbound,
    inbound,
    priceFormatted: formatMoney(flightResult.price, formatCurrency),
    priceAmount: roundAmount(normaliseMoneyAmount(flightResult.price)),
    onClickUrl,
  };
};

type BuildHotelInfoParams = {
  hotelResult: HotelResult;
  hotelContent: HotelContent | null;
  formatCurrency: LocalisationHelpers['formatCurrency'];
  formatNumber: LocalisationHelpers['formatNumber'];
  onClickUrl: string | undefined;
  nights: number;
};

const buildHotelInfo = ({
  formatCurrency,
  formatNumber,
  hotelContent,
  hotelResult,
  nights,
  onClickUrl,
}: BuildHotelInfoParams): HotelInfo => {
  if (!hotelResult.price) {
    logWarnWrapper({
      message: 'Hotel result missing price',
      component: COMPONENT_CAPYBARA_RECOMMENDATIONS,
      hotelId: hotelResult.hotelId || 'unknown',
      hasContent: hotelContent ? '1' : '0',
    });
  }

  const transformed = hotelContent ? transformHotelContent(hotelContent) : null;
  const reviewScore = transformed?.reviewScore;

  const hotelPricePerDay = getPricePerDay(hotelResult.price, nights);

  return {
    imageUrl: transformed?.imageUrl ?? '',
    name: transformed?.name || '',
    distanceLabel: transformed?.distanceLabel ?? '',
    rating: reviewScore == null ? '' : formatNumber(reviewScore),
    priceFormatted: formatMoney(hotelPricePerDay, formatCurrency),
    priceAmount: roundAmount(normaliseMoneyAmount(hotelPricePerDay)),
    onClickUrl,
  };
};

type BuildCarHireInfoParams = {
  carHireResult: CarHireResult;
  formatCurrency: LocalisationHelpers['formatCurrency'];
  onClickUrl: string;
  days: number;
};

const buildCarHireInfo = ({
  carHireResult,
  days,
  formatCurrency,
  onClickUrl,
}: BuildCarHireInfoParams): CarHireInfo => {
  if (!carHireResult.price) {
    logWarnWrapper({
      message: 'Car hire result missing price',
      component: COMPONENT_CAPYBARA_RECOMMENDATIONS,
      carType: carHireResult.carType || 'unknown',
      hasVendorId: carHireResult.vendorId ? '1' : '0',
    });
  }

  const carPricePerDay = getPricePerDay(carHireResult.price, days);

  return {
    imageUrl: carHireResult.imageUri ?? '',
    name: carHireResult.formattedSubCarType || '',
    supplierLogoUrl: carHireResult.vendorId
      ? `${CAR_HIRE_PROVIDER_LOGO_BASE_URL}/${carHireResult.vendorId}.png`
      : undefined,
    priceFormatted: formatMoney(carPricePerDay, formatCurrency),
    priceAmount: roundAmount(normaliseMoneyAmount(carPricePerDay)),
    onClickUrl,
  };
};

export type BuildComboRecommendationParams = {
  recommendation: Recommendation;
  destination: string;
  originId: string | undefined;
  destinationId: string | undefined;
  outboundDate: Date;
  inboundDate: Date | null;
  localisation: Pick<
    LocalisationHelpers,
    'formatCurrency' | 'formatDateAbbr' | 'formatNumber'
  >;
  hotelContent: HotelContent | null;
  stay: StayState;
  impressionId: string;
  getResultUrl: GetResultUrl;
  tripDays: number;
};

export const buildComboRecommendation = ({
  destination,
  destinationId,
  getResultUrl,
  hotelContent,
  impressionId,
  inboundDate,
  localisation,
  originId,
  outboundDate,
  recommendation,
  stay,
  tripDays,
}: BuildComboRecommendationParams): ComboRecommendation | null => {
  let flightResult: FlightResult | undefined;
  let hotelResult: HotelResult | undefined;
  let carHireResult: CarHireResult | undefined;

  recommendation.verticalResults?.forEach((verticalResult) => {
    if (verticalResult.flightResult) flightResult = verticalResult.flightResult;
    if (verticalResult.hotelResult) hotelResult = verticalResult.hotelResult;
    if (verticalResult.carHireResult) {
      carHireResult = verticalResult.carHireResult;
    }
  });

  if (!flightResult || !hotelResult) return null;

  const { formatCurrency, formatNumber } = localisation;

  const flight = buildFlightInfo({
    flightResult,
    formatCurrency,
    onClickUrl: buildFlightRedirectUrl({
      baseUrl: getResultUrl(
        outboundDate,
        inboundDate,
        Boolean(flightResult.direct),
        stay,
      ),
      price: flightResult.price,
    }),
    originId,
    destinationId,
  });

  const hotel = buildHotelInfo({
    hotelResult,
    hotelContent,
    formatCurrency,
    formatNumber,
    onClickUrl: buildHotelRedirectUrl({
      destinationId,
      hotelId: hotelResult.hotelId,
      impressionId,
      inboundDate,
      outboundDate,
      price: getPricePerDay(hotelResult.price, tripDays),
      ...(getFeatureDecision(COMBO_RECOMMENDATION_TYPE, 'cheapest') ===
        'cheapest' && { sortByPriceAsc: true }),
      stay,
    }),
    nights: tripDays,
  });

  const carHire = carHireResult
    ? buildCarHireInfo({
        carHireResult,
        formatCurrency,
        onClickUrl: buildCarHireRedirectUrl({
          carType: carHireResult.carType,
          destinationEntityId: recommendation.destinationCityEntityId,
          inboundDate,
          outboundDate,
          price: getPricePerDay(carHireResult.price, tripDays),
        }),
        days: tripDays,
      })
    : undefined;

  const formatDate = (date: Date) =>
    localisation.formatDateAbbr(date).replaceAll(',', '').replaceAll('.', '');

  const dateRange = inboundDate
    ? `${formatDate(outboundDate)} - ${formatDate(inboundDate)}`
    : formatDate(outboundDate);

  return {
    destination,
    dateRange,
    flight,
    hotel,
    carHire,
    totalPriceFormatted: formatMoney(recommendation.totalPrice, formatCurrency),
    totalPriceAmount: roundAmount(
      normaliseMoneyAmount(recommendation.totalPrice),
    ),
    currency: recommendation.totalPrice?.currencyCode,
    tripDays,
    recommendationType: recommendation.recommendationType,
    originEntityId: recommendation.originCityEntityId,
    destinationEntityId: recommendation.destinationCityEntityId,
    carrierId: flightResult.outbound?.carrierId,
    hotelId: hotelResult.hotelId,
  };
};
